// Hook de datos de Políticas — CONECTADO a `loan-offerings-business` en las tres tabs.
//
// Los PLANES vienen de GET /credit-plans y se ESCRIBEN vía las mutaciones de este archivo (PUT plan
// / segmentación en lote). La BITÁCORA sale de GET /credit-plans/log, en `useCreditPlansLog`: query
// aparte, con sus propios estados, para que un fallo de la bitácora no selle ni bloquee los planes.
// Las Políticas Especiales tienen su propio hook, `api/useStorePolicies.ts`: son otro recurso
// (`/store-policies`), con su RBAC y sus estados de carga, y no entran en este. El catálogo de
// SEGMENTOS sí sale de aquí, y ya es real (`GET /segments`).
//
// La segmentación SCI tiene DOS fuentes: el rango de cada plan (loan-offerings) y los cortes que
// usa el motor de score (loan-config, paramType SCORING). Publicar escribe las dos, o queda una
// banda de SCI donde el cliente cae en un nivel cuyo plan lo rechaza al originar.
//
// Datos SIEMPRE del endpoint (regla del líder): el mock es fallback SOLO cuando la query FALLA
// (`isError`), nunca durante la carga (`isLoading`). Así la pantalla no parpadea de mock a real:
// primera carga → spinner (PoliciesPage gatea `isLoading`); error real → mock + sello "sin backend";
// éxito → datos del endpoint. `keepPreviousData` evita el parpadeo en refetch de fondo.
import { useMemo } from 'react';
import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { ParameterTypeEnum } from '@fiado/type-kit/bin/loanConfig/enums/ParameterTypeEnum';
import {
    bulkUpdateParameters,
    listParameters,
    PARAMETERS_QUERY_KEY,
    type BulkUpdateResult,
    type ParametersByTypeResponse,
} from '@/api/services/loan-config/loan-config.client';
import {
    listCreditPlanLog,
    listCreditPlanLogByPlan,
    listCreditPlans,
    updateCreditPlan,
    type CreditPlanResponse,
} from '@/api/services/loan-offerings/loan-offerings.client';
import { classifyFailure } from '../../creditFile/failures';
import { useSegments } from '../../segments/api/useSegments';
import { resolveSegmentCatalogState } from '../../segments/logic';
import type { SegmentCatalogState } from '../../segments/types';
import { CREDIT_PLANS } from '../data.mock';
import type { HistoryRow } from '../components/HistoryList';
import type { SciCuts } from '../components/SciSegmentBar';
import type { CreditPlan, PolicyLogEntry, Segment } from '../types';
import {
    planToUpdateRequest,
    toAuditReason,
    toPlan,
    toPlanHistoryRow,
    toPolicyLogEntry,
} from './mappers';

const KEYS = {
    plans: ['loan-offerings', 'credit-plans'] as const,
    /** Raíz de las bitácoras; invalidarla alcanza al feed del día y a la de cualquier plan. */
    planLogs: ['loan-offerings', 'credit-plan-log'] as const,
    planLog: (planId: string) => ['loan-offerings', 'credit-plan-log', planId] as const,
    planLogFeed: (day: string) => ['loan-offerings', 'credit-plan-log', 'day', day] as const,
};

const EMPTY_PLANS: CreditPlan[] = [];
const EMPTY_HISTORY: HistoryRow[] = [];
const EMPTY_LOG: PolicyLogEntry[] = [];

/** Parámetros `SCORING` de loan-config con los que el motor decide a qué nivel cae el cliente. */
const ENGINE_CUT_KEYS: Record<keyof SciCuts, string> = {
    start: 'sci_min_eligible',
    bp: 'level_silver_min',
    po: 'level_gold_min',
};

/** Motivo que queda en la auditoría de loan-config cuando la barra mueve los cortes del motor. */
const ENGINE_CUT_REASON = 'Segmentación SCI (Políticas)';

/**
 * Cortes del motor de score leídos de los parámetros `SCORING`. `null` si falta alguno de los
 * tres o no es numérico: con media verdad el aviso de desalineación mentiría.
 */
export function toEngineCuts(grouped: ParametersByTypeResponse | undefined): SciCuts | null {
    const scoring = grouped?.[ParameterTypeEnum.SCORING];
    if (!scoring) return null;
    // El wire manda `effectiveValue` vacío a veces (mismo caso que modelia/logic.ts).
    const read = (key: string): number => {
        const parameter = scoring.find((p) => p.key === key);
        if (!parameter) return NaN;
        return Number(parameter.effectiveValue || parameter.value || parameter.defaultValue);
    };
    const cuts: SciCuts = {
        start: read(ENGINE_CUT_KEYS.start),
        bp: read(ENGINE_CUT_KEYS.bp),
        po: read(ENGINE_CUT_KEYS.po),
    };
    const complete = Number.isFinite(cuts.start) && Number.isFinite(cuts.bp);
    return complete && Number.isFinite(cuts.po) ? cuts : null;
}

/**
 * `canReadSegments` cablea el gate RBAC del catálogo PRESTADO: es de otro dominio y
 * `loan.creditPlan.read` no autoriza a leerlo.
 */
export function usePolicies(canReadSegments = true): {
    plans: CreditPlan[];
    engineCuts: SciCuts | null;
    segments: Segment[];
    segmentCatalogState: SegmentCatalogState;
    isMock: boolean;
    /** El catálogo dio 403: no es que falte backend, es que a este rol le falta el permiso. */
    plansForbidden: boolean;
    plansError: Error | null;
    isLoading: boolean;
} {
    const plansQuery = useQuery({
        queryKey: KEYS.plans,
        queryFn: listCreditPlans,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });

    // Misma queryKey que Configuración global → comparten caché e invalidación.
    // Fuera de `isLoading`/`isMock`: sin `loan.parameter.read` la pantalla sigue en pie, sin cortes.
    const parametersQuery = useQuery({
        queryKey: PARAMETERS_QUERY_KEY,
        queryFn: listParameters,
        staleTime: 60_000,
    });

    // Datos del endpoint apenas hay respuesta; mock solo como fallback de error (no de carga).
    // Memoizado (como useSystemParams): con el structural-sharing de react-query, la referencia solo
    // cambia cuando la data REAL cambia → los efectos de resync de StandardPoliciesTab no parpadean.
    const plans = useMemo(
        () =>
            plansQuery.data
                ? plansQuery.data.map(toPlan)
                : classifyFailure(plansQuery.error) === 'forbidden'
                  ? EMPTY_PLANS
                  : CREDIT_PLANS,
        [plansQuery.data, plansQuery.error],
    );

    const engineCuts = useMemo(() => toEngineCuts(parametersQuery.data), [parametersQuery.data]);

    // El selector de segmento deja de leer una lista clavada en el front: los segmentos son los del
    // tenant, de `GET /segments`. Mientras no cargan, el combo va vacío — no con tres nombres falsos.
    const segmentCatalog = useSegments(canReadSegments);
    const segments = useMemo<Segment[]>(
        () =>
            segmentCatalog.segments.map((segment) => ({
                id: segment.segmentId,
                name: segment.name,
                colorSlot: segment.colorSlot,
                isDefault: segment.isDefault,
                clientCount: segment.clientCount,
                pctTotal: segment.pctTotal,
                countPending: segment.countPending,
            })),
        [segmentCatalog.segments],
    );

    // Por qué el catálogo llegó vacío, si llegó vacío. Sin esto un combo mudo se lee como «este
    // tenant no tiene segmentos», que es una afirmación que nadie hizo.
    const segmentCatalogState = useMemo(
        () =>
            resolveSegmentCatalogState({
                canRead: canReadSegments,
                isLoading: segmentCatalog.isLoading,
                error: segmentCatalog.error,
            }),
        [canReadSegments, segmentCatalog.isLoading, segmentCatalog.error],
    );

    // Un 403 NO cae al mock: enseñarle 3 planes falsos y editables a quien no puede leer el
    // catálogo le hace creer que está configurando el motor de crédito. El mock queda solo para
    // la falla de lectura, que es lo que siempre significó el sello «sin backend».
    const plansForbidden = classifyFailure(plansQuery.error) === 'forbidden';

    return {
        plans,
        engineCuts,
        segments,
        segmentCatalogState,
        isMock: plansQuery.isError && !plansForbidden,
        plansForbidden,
        plansError: plansQuery.error,
        isLoading: plansQuery.isLoading,
    };
}

/** Un corte SCI a aplicar a un plan (segmentación) — el planId identifica el plan a parchar. */
export interface SegmentEdit {
    planId: string;
    minSciScore: number;
    maxSciScore: number;
}

/** Lo que publica la barra: los planes a parchar y los cortes que van al motor de score. */
export interface PublishSegmentationInput {
    edits: SegmentEdit[];
    cuts: SciCuts;
    /** Motivo que tecleó el operador; sin él, los parámetros del motor asientan `ENGINE_CUT_REASON`. */
    reason?: string;
}

/** Resultado de las dos escrituras: los planes guardados y el lote de parámetros del motor. */
export interface PublishSegmentationResult {
    plans: CreditPlanResponse[];
    params: BulkUpdateResult;
}

/**
 * Publica los cambios económicos de UN plan → PUT /credit-plans/{planId}. El backend versiona
 * (version++) y devuelve el plan; al terminar invalidamos la query para releer la verdad del server.
 */
export function useUpdateCreditPlan() {
    const qc = useQueryClient();
    return useMutation<
        CreditPlanResponse,
        Error,
        { planId: string; plan: CreditPlan; reason?: string }
    >({
        mutationFn: ({ planId, plan, reason }) =>
            updateCreditPlan(planId, planToUpdateRequest(plan, reason)),
        // La bitácora también: publicar deja un asiento nuevo y sin esto el historial sigue viejo.
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: KEYS.plans }),
                qc.invalidateQueries({ queryKey: KEYS.planLogs }),
            ]);
        },
    });
}

/**
 * Publica la segmentación en sus DOS fuentes: un PUT por plan afectado y, después, un lote a los
 * tres parámetros `SCORING` del motor. Los parámetros se escriben aunque no haya planes afectados.
 * NO es atómico: el backend F1 no expone un update transaccional multi-plan. Si un PUT falla, los
 * previos ya se aplicaron y el motor no se toca; la invalidación relee el estado real.
 */
export function usePublishSegmentation() {
    const qc = useQueryClient();
    return useMutation<PublishSegmentationResult, Error, PublishSegmentationInput>({
        mutationFn: async ({ edits, cuts, reason }) => {
            // Un solo motivo para las dos escrituras: los planes y el motor cambian por lo mismo, y
            // dos textos distintos en la auditoría se leen como dos cambios sin relación.
            const auditReason = toAuditReason(reason);
            const plans = await Promise.all(
                edits.map((e) =>
                    updateCreditPlan(e.planId, {
                        minSciScore: e.minSciScore,
                        maxSciScore: e.maxSciScore,
                        ...(auditReason === undefined ? {} : { reason: auditReason }),
                    }),
                ),
            );
            const paramReason = auditReason ?? ENGINE_CUT_REASON;
            const params = await bulkUpdateParameters([
                {
                    paramType: ParameterTypeEnum.SCORING,
                    key: ENGINE_CUT_KEYS.start,
                    value: String(cuts.start),
                    reason: paramReason,
                },
                {
                    paramType: ParameterTypeEnum.SCORING,
                    key: ENGINE_CUT_KEYS.bp,
                    value: String(cuts.bp),
                    reason: paramReason,
                },
                {
                    paramType: ParameterTypeEnum.SCORING,
                    key: ENGINE_CUT_KEYS.po,
                    value: String(cuts.po),
                    reason: paramReason,
                },
            ]);
            return { plans, params };
        },
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: KEYS.plans }),
                qc.invalidateQueries({ queryKey: KEYS.planLogs }),
                qc.invalidateQueries({ queryKey: PARAMETERS_QUERY_KEY }),
            ]);
        },
    });
}

export interface CreditPlanLogResult {
    rows: HistoryRow[];
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
}

/**
 * La bitácora de UN plan → GET /credit-plans/{planId}/log, ya traducida al renglón del historial.
 * Sin `planId` (catálogo mock) la query ni se dispara: no hay plan del que leer asientos.
 */
export function useCreditPlanLog(planId: string): CreditPlanLogResult {
    const enabled = planId !== '';
    const query = useQuery({
        queryKey: KEYS.planLog(planId),
        queryFn: () => listCreditPlanLogByPlan(planId),
        enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });

    const rows = useMemo(
        () => (query.data ? query.data.entries.map(toPlanHistoryRow) : EMPTY_HISTORY),
        [query.data],
    );

    // Con la query apagada `isLoading` de react-query se queda en `true`: sin esto, un plan sin
    // id pintaría un cargando eterno en vez del vacío.
    return {
        rows,
        isLoading: enabled && query.isLoading,
        error: enabled ? query.error : null,
        refetch: () => {
            void query.refetch();
        },
    };
}

export interface CreditPlansLogResult {
    /** Los asientos de TODAS las páginas traídas hasta ahora, no solo la última. */
    entries: PolicyLogEntry[];
    /** El día trae más asientos de los que se han cargado. */
    hasMore: boolean;
    /** Trae la siguiente página y la suma a la pila. */
    loadMore: () => void;
    isLoadingMore: boolean;
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
}

/**
 * El feed de la bitácora de TODOS los planes → GET /credit-plans/log (tab Auditoría), del `day`
 * pedido. El backend pagina por cursor y aquí se PASEA: cada página se suma a la pila en vez de
 * reemplazarla. Antes se pedía solo la primera y el asiento 101 era inalcanzable.
 *
 * Query PROPIA y estados propios, fuera del `isMock`/`isLoading` de los planes: combinarlos
 * bloquearía la tab Estándar mientras carga la bitácora, y sellaría la pantalla entera cuando falla
 * una sola de las dos.
 */
export function useCreditPlansLog(day: string): CreditPlansLogResult {
    const query = useInfiniteQuery({
        queryKey: KEYS.planLogFeed(day),
        queryFn: ({ pageParam }) => listCreditPlanLog({ day, cursor: pageParam }),
        initialPageParam: undefined as string | undefined,
        // `null` es «última página» en el wire; `getNextPageParam` corta con `undefined`.
        getNextPageParam: (last) => last.nextCursor ?? undefined,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });

    // useMemo obligatorio: sin él el flatMap devuelve un array nuevo en cada render y los
    // consumidores que comparan por referencia se re-renderizan sin fin.
    const entries = useMemo(
        () => query.data?.pages.flatMap((page) => page.entries.map(toPolicyLogEntry)) ?? EMPTY_LOG,
        [query.data],
    );

    return {
        entries,
        hasMore: query.hasNextPage,
        loadMore: () => {
            void query.fetchNextPage();
        },
        isLoadingMore: query.isFetchingNextPage,
        isLoading: query.isLoading,
        error: query.error,
        refetch: () => {
            void query.refetch();
        },
    };
}
